import type { WritableAtom } from "nanostores";

import { persistentAtom } from "@nanostores/persistent";
import { createContext, useContext } from "react";

export type SidebarBehavior = {
  collapsed: boolean;
  itemTitle: (label: string) => string | undefined;
  labelClassName: string;
  showLabels: boolean;
  sidebarWidthClassName: string;
  toggleCollapsed: () => void;
};

export const SidebarBehaviorContext = createContext<null | SidebarBehavior>(
  null,
);

const persistentBooleanAtoms = new Map<string, WritableAtom<boolean>>();

/**
 * Returns a memoizable persistent boolean atom for a given key.
 * Uses localStorage under the hood (via @nanostores/persistent) to survive refreshes.
 */
export const getPersistentBooleanAtom = (
  key: string,
  defaultValue: boolean,
): WritableAtom<boolean> => {
  const existing = persistentBooleanAtoms.get(key);
  if (existing) return existing;

  const created = persistentAtom<boolean>(key, defaultValue, {
    decode: JSON.parse,
    encode: JSON.stringify,
  });

  persistentBooleanAtoms.set(key, created);
  return created;
};

export const useSidebarBehavior = (): SidebarBehavior => {
  const ctx = useContext(SidebarBehaviorContext);
  if (!ctx) {
    throw new Error(
      "useSidebarBehavior must be used inside <CollapsibleSidebar />",
    );
  }
  return ctx;
};
